import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Grid, Paper, Typography, Tabs, Tab } from "@material-ui/core";
import DirectionsCarOutlinedIcon from "@material-ui/icons/DirectionsCarOutlined";
import VehicleDetails from "../VehicleDetailsNew";
import PlantDetails from "../PlantDetailsNew";
import ContentUpload from "../ContentUploadNew";
import CreateJobInspection from "../CreateInspectionNew";

// ----- Design Tokens -----
const ACCENT = "#1d4ed8";
const ACCENT_DARK = "#1e40af";
const SURFACE = "#ffffff";
const PAGE_BG = "#f8fafc";
const BORDER = "#e5e7eb";
const TEXT_TITLE = "#0f172a";
const TEXT_MUTED = "#64748b";

const COLORS = {
  blue100: "#dbeafe",
  blue700: "#1d4ed8",
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: PAGE_BG,
    minHeight: "calc(100vh - 127px)",
    fontFamily: theme.typography.fontFamily,
  },
  pageHeaderLeft: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1.5),
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    flexShrink: 0,
  },
  subtitle: {
    fontSize: "0.82rem !important",
    color: TEXT_MUTED,
    marginTop: 2,
  },
  paper: {
    backgroundColor: SURFACE,
    borderRadius: 16,
    overflow: "hidden",
    border: `1px solid ${BORDER}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.05)",
    minHeight: "60vh",
  },
  tabs: {
    borderBottom: `1px solid ${BORDER}`,
    backgroundColor: "rgba(241,245,249,0.5)",
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      minWidth: 160,
      fontSize: "0.85rem !important",
      color: TEXT_MUTED,
    },
    "& .Mui-selected": {
      color: ACCENT,
      backgroundColor: COLORS.blue100,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: ACCENT,
      height: 3,
    },
  },
  tabPanel: {
    padding: theme.spacing(1.5),
  },
  iconFontSize: {
    fontSize: 22,
  },
  pageTitle: {
    fontSize: "1.4rem !important",
  },
}));

const TabPanel = ({ value, index, children, className }) => {
  if (value !== index) return null;
  return (
    <div
      role="tabpanel"
      id={`vij-tabpanel-${index}`}
      aria-labelledby={`vij-tab-${index}`}
      className={className}
    >
      {children}
    </div>
  );
};

const a11yProps = (index) => ({
  id: `vij-tab-${index}`,
  "aria-controls": `vij-tabpanel-${index}`,
});

const VehicleInspectionJob = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedVehicleUid, setSelectedVehicleUid] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedPlantUid, setSelectedPlantUid] = useState("");
  const [selectedPlant, setSelectedPlant] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleVehicleSelect = (uid, vehicle) => {
    setSelectedVehicleUid(uid);
    setSelectedVehicle(vehicle || null);
  };

  const handleVehicleUpdated = (updates) => {
    setSelectedVehicle((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const handlePlantSelect = (uid, plant) => {
    setSelectedPlantUid(uid);
    setSelectedPlant(plant || null);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box className={classes.pageHeaderLeft}>
            <Box className={classes.headerIcon}>
              <DirectionsCarOutlinedIcon className={classes.iconFontSize} style={{ color: "white" }} />
            </Box>
            <div>
              <Typography>
                <strong className={classes.pageTitle} style={{ color: TEXT_TITLE }}>
                  Vehicle Inspection Job
                </strong>
              </Typography>
            </div>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper className={classes.paper} elevation={0}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              className={classes.tabs}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Vehicle inspection job tabs"
            >
              <Tab label="Vehicle Details" {...a11yProps(0)} />
              <Tab label="Plant Details" {...a11yProps(1)} />
              <Tab label="Content Upload" {...a11yProps(2)} />
              <Tab label="Create Job Inspection" {...a11yProps(3)} />
            </Tabs>

            <TabPanel
              value={activeTab}
              index={0}
              className={classes.tabPanel}
            >
              <VehicleDetails
                selectedUid={selectedVehicleUid}
                onSelectedChange={handleVehicleSelect}
              />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={1}
              className={classes.tabPanel}
            >
              <PlantDetails
                selectedUid={selectedPlantUid}
                onSelectedChange={handlePlantSelect}
              />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={2}
              className={classes.tabPanel}
            >
              <ContentUpload
                selectedVehicle={selectedVehicle}
                onVehicleUpdated={handleVehicleUpdated}
              />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={3}
              className={classes.tabPanel}
            >
              <CreateJobInspection
                selectedVehicle={selectedVehicle}
                selectedPlant={selectedPlant}
              />
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default VehicleInspectionJob;
